import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  chainGlyph,
  chainName,
  isTestnet,
} from '../_shared/wallet-app/chains';
import { BRAND } from '../theme';

// ChainPicker
//
// Renders a chain selector with glyph + name + balance line + testnet pill.
// Used for both source and destination. Incompatible pairs (same chain id
// as the partner) are disabled — that's how we enforce "src != dst" in the
// UI without showing an error message.
//
// Props:
//   label         "Source" | "Destination"
//   value         Selected chain id (number)
//   onChange      (chainId) => void
//   options       Array of { chainId, available } — drives the dropdown
//   partnerId     The other side's chain id; this entry is disabled
//   balanceLine   Optional secondary line (e.g. "Balance: 50.000 kUSD" or
//                 "Receive: 49.975 kUSD")
//   testIdSuffix  Optional data-testid suffix for vitest

export default function ChainPicker({
  label,
  value,
  onChange,
  options = [],
  partnerId,
  balanceLine,
  testIdSuffix = '',
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        borderRadius: 2,
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: `1px solid ${BRAND.border}`,
        transition: 'border-color 120ms ease',
        '&:hover': { borderColor: BRAND.violet },
      }}
      data-testid={`chain-picker-${testIdSuffix || label.toLowerCase()}`}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="caption" sx={{ color: BRAND.textSecondary, fontWeight: 600 }}>
          {label.toUpperCase()}
        </Typography>
        {value !== '' && value != null && isTestnet(value) && (
          <Chip
            label="Testnet — no real value"
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              backgroundColor: BRAND.warning + '22',
              color: BRAND.warning,
              border: `1px solid ${BRAND.warning}44`,
            }}
          />
        )}
      </Stack>
      <FormControl fullWidth size="small" variant="standard">
        <Select
          disableUnderline
          value={value ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
          renderValue={(v) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ fontSize: '1.4rem', lineHeight: 1 }}>{chainGlyph(v)}</Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                {v ? chainName(v) : 'Select chain'}
              </Typography>
            </Stack>
          )}
          inputProps={{ 'data-testid': `${testIdSuffix || label.toLowerCase()}-select` }}
          sx={{
            '& .MuiSelect-select': { py: 0.5 },
            '& .MuiSelect-icon': { color: BRAND.textSecondary },
          }}
        >
          {options.length === 0 && (
            <MenuItem disabled value="">
              No chains available
            </MenuItem>
          )}
          {options.map((opt) => {
            const disabled = !opt.available || opt.chainId === partnerId;
            return (
              <MenuItem
                key={opt.chainId}
                value={opt.chainId}
                disabled={disabled}
                data-testid={`${testIdSuffix || label.toLowerCase()}-option-${opt.chainId}`}
              >
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%' }}>
                  <Box sx={{ fontSize: '1.2rem' }}>{chainGlyph(opt.chainId)}</Box>
                  <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {chainName(opt.chainId)}
                  </Typography>
                  {opt.chainId === partnerId && (
                    <Typography variant="caption" sx={{ color: BRAND.textSecondary }}>
                      same as partner
                    </Typography>
                  )}
                  {!opt.available && opt.chainId !== partnerId && (
                    <Typography variant="caption" sx={{ color: BRAND.textSecondary }}>
                      not deployed
                    </Typography>
                  )}
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {balanceLine && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 1, color: BRAND.textSecondary }}
        >
          {balanceLine}
        </Typography>
      )}
    </Box>
  );
}
